import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PageTitle from '../../components/PageTitle';
import './PatientList.css';
import styles from './MessagesOverview.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/';

const MessagesOverview = () => {
    const navigate = useNavigate();
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchPatientsWithMessages();
    }, []);

    const fetchPatientsWithMessages = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await axios.get(`${API_URL}messages/patients-with-messages`, {
                headers: { 'Accept': 'application/json' }
            });
            setPatients(response.data.patients || []);
        } catch (err) {
            console.error('Error fetching patients:', err);
            setError(err.response?.data?.error || 'Failed to load patients with messages');
            if (err.response?.status === 401) {
                navigate('/control/login');
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePatientClick = (patientId) => {
        navigate(`/control/patients/${patientId}/messages`);
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const truncateMessage = (message, maxLength = 50) => {
        if (!message) return '';
        if (message.length <= maxLength) return message;
        return message.substring(0, maxLength) + '...';
    };

    if (loading) {
        return (
            <div className="patient-list-container">
                <div className="container py-4">
                    <PageTitle title="Messages" />
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading messages...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="patient-list-container">
                <div className="container py-4">
                    <PageTitle title="Messages" />
                    <div className="alert alert-danger">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="patient-list-container">
            <div className="container py-4">
                <PageTitle title="Messages" />
                
                <div className="card patient-list-card">
                    <div className="card-header patient-list-header">
                        <div className="d-flex justify-content-between align-items-center">
                            <h2 className="mb-0">
                                <i className="fas fa-comments me-2"></i>
                                Patient Messages
                            </h2>
                            <button 
                                className="btn btn-sm btn-light"
                                onClick={fetchPatientsWithMessages}
                            >
                                <i className="fas fa-sync-alt me-1"></i>
                                Refresh
                            </button>
                        </div>
                    </div>

                    <div className="card-body patient-list-body p-0">
                        {patients.length === 0 ? (
                            <div className="text-center py-5">
                                <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                                <p className="text-muted">No messages yet</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table patient-table mb-0">
                                    <thead>
                                        <tr>
                                            <th>Patient Name</th>
                                            <th className={styles.hideOnMobile}>Phone Number</th>
                                            <th>Time</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {patients.map((patient) => (
                                            <tr 
                                                key={patient.id}
                                                className={patient.has_unseen ? styles.tableRowUnseen : ''}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handlePatientClick(patient.id)}
                                            >
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <div className={`${styles.patientAvatar} me-2`}>
                                                            <i className="fas fa-user"></i>
                                                        </div>
                                                        <div>
                                                            <strong>{patient.name}</strong>
                                                            {patient.has_unseen && (
                                                                <span className="badge bg-danger ms-2">
                                                                    {patient.unseen_count} new
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className={`text-muted ${styles.hideOnMobile}`}>{patient.phone_number}</td>
                                                <td className="text-muted">
                                                    {patient.last_message && formatTime(patient.last_message.created_at)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MessagesOverview;
